/** @format */
import {
    Choice,
    ChoiceLabelSize,
    ChoiceVariant,
} from '@mpb-com/feather/components/atoms/choice';
import { Input } from '@mpb-com/feather/components/molecules/input';
import { InputProps } from '@mpb-com/feather/components/molecules/input/types';
import { useTranslation } from 'next-i18next';
import React, {
    ChangeEvent,
    FunctionComponent,
    KeyboardEvent,
    useContext,
    useEffect,
    useState,
} from 'react';
import styled from 'styled-components';

import {
    centToDecimal,
    decimalToCent,
    getCurrencySymbol,
} from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';

import { SearchFilterMenuOptionList } from '@toucan/client/features/search/components/option-list';
import { FilterOption } from '@toucan/client/features/search/config/types';

import {
    CustomRangeField,
    DEFAULT_TEST_ID,
    MAX_CUSTOM_RANGE,
    PriceFilterOption,
} from './constants';
import { styles } from './price-options.styles';
import { SearchFilterMenuPriceOptionsProps } from './types';

const StyledSearchFilterMenuPriceOptions = styled.div`
    && {
        ${styles}
    }
`;

export const SearchFilterMenuPriceOptions: FunctionComponent<
    SearchFilterMenuPriceOptionsProps
> = ({
    onSelect,
    onClear,
    filter,
    isLoading = false,
    displayOptionQuantity = true,
    selectedOptions,
    selectedVariant = PriceFilterOption.GROUP,
    onSelectVariant,
    autoFocusPriceGroup,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const currencySymbol = getCurrencySymbol(
        activeMarket.iso,
        activeMarket.currency
    );

    const [customRange, setCustomRange] = useState(getInitialCustomRange);

    function clearCustomRangeInput() {
        setCustomRange({ from: '', to: '' });
    }

    function serializeCustomRangeValue(
        value: string | number,
        convertFunction: (v: number) => number
    ) {
        if (!value || isNaN(Number(value))) {
            return '';
        }

        return Math.min(convertFunction(Number(value)), MAX_CUSTOM_RANGE);
    }

    function getInitialCustomRange() {
        if (selectedVariant === PriceFilterOption.GROUP) {
            return { from: '', to: '' };
        }

        const value = selectedOptions?.[0]?.value ?? '';
        const [from, to] = `${value}`.split('-');

        return {
            from: serializeCustomRangeValue(from, centToDecimal),
            to: serializeCustomRangeValue(to, centToDecimal),
        };
    }

    function getCustomRangeItem(from: string | number, to: string | number) {
        const serializedFrom = serializeCustomRangeValue(from, decimalToCent);
        const serializedTo = serializeCustomRangeValue(to, decimalToCent);

        if (serializedFrom || serializedTo) {
            return { value: `${serializedFrom || 0}-${serializedTo}` };
        }

        return null;
    }

    function onCustomRangeInputFocus() {
        onSelectVariant(PriceFilterOption.RANGE);
        if (!(customRange.from || customRange.to)) {
            onClear?.();
        }
    }

    function onCustomRangeInputKeyPress(
        event: KeyboardEvent<HTMLInputElement>
    ) {
        // HTML number inputs still allow these keys although here we don't want to
        const disallowedKeys = ['e', '-', '+'];

        if (disallowedKeys.includes(event.key)) {
            event.preventDefault();
        }
    }

    function onCustomRangeChange(value: string, field: CustomRangeField) {
        const from = field === CustomRangeField.FROM ? value : customRange.from;
        const to = field === CustomRangeField.TO ? value : customRange.to;

        setCustomRange({ from, to });

        const customRangeItem = getCustomRangeItem(from, to);

        if (!customRangeItem) {
            onClear?.();
            return;
        }

        onSelect?.(customRangeItem, { overwrite: true });
    }

    function onPriceGroupSelect(value: FilterOption) {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const overwrite = selectedVariant === PriceFilterOption.RANGE;
        onSelect?.(value, { overwrite });
        onSelectVariant(PriceFilterOption.GROUP);
        clearCustomRangeInput();
    }

    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    function onRadioSelect(e: ChangeEvent<HTMLInputElement>) {
        const option = e.target.value as PriceFilterOption;
        onSelectVariant(option);
        onClear?.();
    }

    useEffect(() => {
        if (!selectedOptions?.length) {
            clearCustomRangeInput();
        }
    }, [selectedOptions]);

    const commonInputProps: Partial<InputProps> = {
        className: 'input',
        type: 'number',
        prefixIcon: currencySymbol,
        onFocus: onCustomRangeInputFocus,
        min: 0,
        onKeyPress: onCustomRangeInputKeyPress,
    };

    return (
        <StyledSearchFilterMenuPriceOptions data-testid={dataTestId}>
            <Choice
                className="option"
                variant={ChoiceVariant.RADIO}
                name="price-options-variant"
                label={t('Filter by price group')}
                value={PriceFilterOption.GROUP}
                checked={selectedVariant === PriceFilterOption.GROUP}
                onChange={onRadioSelect}
                labelSize={ChoiceLabelSize.SMALL}
            />
            <SearchFilterMenuOptionList
                onSelect={onPriceGroupSelect}
                filter={filter}
                isLoading={isLoading}
                displayOptionQuantity={displayOptionQuantity}
                selectedOptions={
                    selectedVariant === PriceFilterOption.GROUP
                        ? selectedOptions
                        : []
                }
                autoFocus={
                    autoFocusPriceGroup ??
                    selectedVariant === PriceFilterOption.GROUP
                }
            />
            <Choice
                className="option"
                variant={ChoiceVariant.RADIO}
                name="price-options-variant"
                label={t('Customise range')}
                value={PriceFilterOption.RANGE}
                checked={selectedVariant === PriceFilterOption.RANGE}
                onChange={onRadioSelect}
                labelSize={ChoiceLabelSize.SMALL}
            />
            <div className="custom-range-inputs">
                <Input
                    data-testid={`${dataTestId}__from-input`}
                    label={t('from')}
                    value={customRange.from}
                    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
                    onChange={(e) =>
                        onCustomRangeChange(
                            e.currentTarget.value,
                            CustomRangeField.FROM
                        )
                    }
                    {...commonInputProps}
                />
                <Input
                    data-testid={`${dataTestId}__to-input`}
                    label={t('to')}
                    value={customRange.to}
                    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
                    onChange={(e) =>
                        onCustomRangeChange(
                            e.currentTarget.value,
                            CustomRangeField.TO
                        )
                    }
                    {...commonInputProps}
                />
            </div>
        </StyledSearchFilterMenuPriceOptions>
    );
};
